/**
 * Joint-replacement point scores (THA: Table 17-34, TKA: Table 17-35 -> Table 17-33).
 *
 * Mirrors QME - Tool src/components/physical-exam/pointScoreConfig.ts: the same
 * answer options (each option's value is what the ported calculator parses), the
 * same defaults (the best, "good result" answers) and the same pre-fill from the
 * joint's ROM exam (autoFromRom). The math is the verbatim ports in tka.ts / tha.ts.
 *
 * Data lives on the region: examData.<region>.<key>_applies_<side> (boolean) and
 * examData.<region>.<key>_<side> (the answers). A joint marked as replaced is rated
 * by its point score INSTEAD of its range of motion.
 */
import { calculateTKAImpairment } from './tka';
import { calculateTHAImpairment } from './tha';

export type Side = 'right' | 'left';
export interface PSOption { label: string; value: string }
export type PSField =
  | { field: string; label: string; kind: 'select'; options: PSOption[] }
  | { field: string; label: string; kind: 'number'; unit?: string };
export interface PSSection { title: string; fields: PSField[] }

export interface ReplacementConfig {
  key: 'tka' | 'tha';
  region: 'knees' | 'hips';
  title: string;
  table: string;
  sections: PSSection[];
  defaults: (regionData: any, side: Side) => Record<string, any>;
  calc: (answers: Record<string, any>, side: Side) => { wpi: number; narrative: string; breakdown?: Record<string, any> };
}

const o = (label: string, value = label): PSOption => ({ label, value });
const sel = (field: string, label: string, options: PSOption[]): PSField => ({ field, label, kind: 'select', options });
const num = (field: string, label: string, unit = '°'): PSField => ({ field, label, kind: 'number', unit });
const thr = (field: string, label: string, good: string, bad: string) => sel(field, label, [o(good), o(bad)]);
const numOrNull = (v: any): number | null => (v === null || v === undefined || v === '' || Number.isNaN(Number(v)) ? null : Number(v));

// ── TKA (Table 17-35) ────────────────────────────────────────────────────────
export const TKA: ReplacementConfig = {
  key: 'tka',
  region: 'knees',
  title: 'Total knee replacement',
  table: 'Table 17-35 point score',
  sections: [
    { title: 'Pain', fields: [sel('pain', 'Pain', ['None', 'Mild', 'Stairs only', 'Walking and stairs', 'Moderate - occasional', 'Moderate continual', 'Severe'].map((x) => o(x)))] },
    { title: 'Range of motion (arc ÷ 5)', fields: [num('rom_min', 'ROM min (contracture)'), num('rom_max', 'ROM max (flexion)')] },
    {
      title: 'Stability',
      fields: [
        sel('stability_ap', 'A-P stability', [o('< 5 mm', '<5'), o('5–9 mm', '7'), o('> 9 mm', '12')]),
        sel('stability_mediolateral', 'Mediolateral stability', [o('5° or less', '5'), o('6–9°', '7'), o('10–14°', '12'), o('15° or more', '15')]),
      ],
    },
    { title: 'Deductions', fields: [num('flexion_contracture', 'Flexion contracture'), num('extensor_lag', 'Extensor lag'), num('alignment', 'Alignment (varus/valgus)')] },
  ],
  // Best answers + ROM from the knee exam (QME - Tool TKA_CONFIG.autoFromRom).
  defaults: (knees, side) => {
    const contracture = numOrNull(knees?.[`${side}_flexion_contracture_passive`]) ?? 0;
    return {
      pain: 'None',
      rom_min: contracture,
      rom_max: numOrNull(knees?.[`${side}_flexion`]),
      stability_ap: '<5',
      stability_mediolateral: '5',
      flexion_contracture: contracture,
      extensor_lag: numOrNull(knees?.[`${side}_extensor_lag`]) ?? 0,
      alignment: numOrNull(knees?.[`${side}_varus`]) || numOrNull(knees?.[`${side}_valgus`]) || 0,
    };
  },
  calc: (answers, side) => calculateTKAImpairment({ extractedData: { knees: { [`tka_${side}`]: answers } }, side }),
};

// ── THA (Table 17-34) ────────────────────────────────────────────────────────
export const THA: ReplacementConfig = {
  key: 'tha',
  region: 'hips',
  title: 'Total hip replacement',
  table: 'Table 17-34 point score',
  sections: [
    { title: 'Pain', fields: [sel('pain', 'Pain', ['None', 'Slight', 'Moderate occasional', 'Moderate', 'Marked'].map((x) => o(x)))] },
    {
      title: 'Function',
      fields: [
        sel('limp', 'Limp', ['None', 'Slight', 'Moderate', 'Severe'].map((x) => o(x))),
        sel('supportive_device', 'Supportive device', ['None', 'Cane for long walks', 'Cane', 'One crutch', 'Two canes', 'Two crutches'].map((x) => o(x))),
        sel('distance_walked', 'Distance walked', ['Unlimited', 'Six blocks', 'Three blocks', 'Indoors', 'In bed or chair'].map((x) => o(x))),
      ],
    },
    {
      title: 'Activities',
      fields: [
        sel('stairs_climbing', 'Stairs', ['Normal', 'Using railing', 'Cannot climb readily', 'Unable to climb'].map((x) => o(x))),
        sel('shoes_socks', 'Shoes & socks', ['With ease', 'With difficulty', 'Unable to do'].map((x) => o(x))),
        sel('sitting', 'Sitting', ['Any chair', 'High chair', 'Unable to sit comfortably'].map((x) => o(x))),
        sel('public_transportation', 'Public transportation', ['Able to use', 'Unable to use'].map((x) => o(x))),
      ],
    },
    {
      title: 'Deformity (each favorable = 1 pt)',
      fields: [
        thr('fixed_adduction', 'Fixed adduction', '<10°', '≥10°'),
        thr('fixed_internal_rotation', 'Fixed internal rotation', '<10°', '≥10°'),
        thr('fixed_external_rotation', 'Fixed external rotation', '<10°', '≥10°'),
        thr('flexion_contracture', 'Flexion contracture', '<15°', '≥15°'),
        thr('leg_length_discrepancy', 'Leg-length discrepancy', '<1.5 cm', '≥1.5 cm'),
      ],
    },
    {
      title: 'Range of motion (each favorable = 1 pt)',
      fields: [
        thr('flexion', 'Flexion', '>90°', '≤90°'),
        thr('abduction', 'Abduction', '>15°', '≤15°'),
        thr('adduction', 'Adduction', '>15°', '≤15°'),
        thr('external_rotation', 'External rotation', '>30°', '≤30°'),
        thr('internal_rotation', 'Internal rotation', '>15°', '≤15°'),
      ],
    },
  ],
  // Best answers + thresholds from the hip exam (QME - Tool THA_CONFIG.autoFromRom).
  defaults: (hips, side) => {
    const g = (k: string) => numOrNull(hips?.[`${side}_${k}`]);
    const fl = g('flexion'), ir = g('internal_rotation'), er = g('external_rotation'), ad = g('adduction'), fc = g('extension_contracture');
    return {
      pain: 'None', limp: 'None', supportive_device: 'None', distance_walked: 'Unlimited',
      stairs_climbing: 'Normal', shoes_socks: 'With ease', sitting: 'Any chair', public_transportation: 'Able to use',
      fixed_adduction: '<10°', fixed_internal_rotation: '<10°', fixed_external_rotation: '<10°',
      flexion_contracture: fc == null || fc < 15 ? '<15°' : '≥15°',
      leg_length_discrepancy: '<1.5 cm',
      flexion: fl == null || fl > 90 ? '>90°' : '≤90°',
      abduction: '>15°', // no plain hip ROM motion for this; left to the examiner (as in QME - Tool)
      adduction: ad == null || ad > 15 ? '>15°' : '≤15°',
      external_rotation: er == null || er > 30 ? '>30°' : '≤30°',
      internal_rotation: ir == null || ir > 15 ? '>15°' : '≤15°',
    };
  },
  calc: (answers, side) => calculateTHAImpairment({ extractedData: { hips: { [`tha_${side}`]: answers } }, side }),
};

/** The replacement rating for one side, or null if that joint isn't marked replaced. */
export function replacementRating(config: ReplacementConfig, regionData: any, side: Side) {
  if (!regionData?.[`${config.key}_applies_${side}`]) return null;
  const answers = regionData[`${config.key}_${side}`] || config.defaults(regionData, side);
  return config.calc(answers, side);
}
